import { useEffect } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import useInactivityTimer from '../../../hooks/useInactivityTimer';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { useSendLogoutMutation } from '../authApiSlice';
import { selectCurrentUser } from '../authSlice';
import { logOut } from '../authSlice';

/**
 * Component to handle automatic logout after user inactivity
 * Should be rendered only when user is authenticated
 */
const InactivityLogout = () => {
    const user = useSelector(selectCurrentUser);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [sendLogout] = useSendLogoutMutation();
    const [rememberUser] = useLocalStorage('rememberMe', false);

    const handleInactivity = async () => {
        if (!user) return;

        try {
            // Save username to localStorage if "remember me" was checked
            const currentRememberMe = localStorage.getItem('rememberMe');
            if (currentRememberMe === 'true' && user.username) {
                localStorage.setItem('savedUsername', user.username);
            }

            // Set flag to show auto-logout message
            localStorage.setItem('autoLoggedOut', 'true');
            localStorage.setItem('autoLogoutTime', new Date().toISOString());

            // Send logout request to server with isAutoLogout flag
            await sendLogout({ isAutoLogout: true }).unwrap();

            dispatch(logOut());

            // Navigate to login page
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Error during auto-logout:', error);
            // Even if server logout fails, logout locally
            dispatch(logOut());
            navigate('/login', { replace: true });
        }
    };

    // Use the inactivity timer hook only if user is logged in
    useInactivityTimer(
        handleInactivity,
        process.env.REACT_APP_INACTIVITY_TIMEOUT
    );

    return <Outlet />;
};

export default InactivityLogout;
